import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { cn } from '../../shared/lib/cn';
import { useDebouncedValue } from '../../shared/lib/use-debounced-value';
import { Button } from '../../shared/ui/button';
import { GROUP_LABEL } from '../../shared/ui/group-label';
import { ClockIcon, GrowthIcon, SearchIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import {
  formatBytes,
  retentionKeys,
  storageFlows,
  warningText,
  type TFlowLevel,
  type TStorageLevels,
} from '../../entities/retention';
import { FlowPeriod } from './flow-period';

const TOP = 20;
const SHOWN = 8;

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const Row = ({ flow, rank }: { flow: TFlowLevel; rank: number | null }) => (
  <div className="border-t border-border-row px-card">
    <div className="flex min-h-row-xl items-center gap-3">
      <span className="w-col-rank shrink-0 font-mono text-meta text-faint">{rank ?? ''}</span>
      <Link
        to="/flows/$flowId"
        params={{ flowId: flow.flowId }}
        className="min-w-0 flex-1 truncate text-data text-primary hover:underline"
      >
        {flow.name}
      </Link>
      <span className="w-col-inherits shrink-0 truncate text-data text-muted">
        {flow.folderName ?? 'Not in a folder'}
      </span>
      <span className="w-col-keeps shrink-0">
        <FlowPeriod flow={flow} />
      </span>
      <span className="w-col-size shrink-0 text-right font-mono text-meta text-primary">
        {flow.published || flow.bytes > 0 ? formatBytes(flow.bytes) : 'draft'}
      </span>
      <span className="w-col-growth shrink-0 text-right font-mono text-meta text-muted">
        +{formatBytes(flow.growth30dBytes)}
      </span>
    </div>
    {(flow.warning || flow.fastGrowthRatio !== null) && (
      <div className="flex flex-col gap-hair pb-inset pl-8">
        {flow.warning && (
          <p className="flex gap-label text-meta leading-17 text-motion">
            <ClockIcon size="sm" className="mt-hair" />
            {warningText(flow.warning, flow.keeps.period)}
          </p>
        )}
        {flow.fastGrowthRatio !== null && (
          <p className="flex gap-label text-meta leading-17 text-motion">
            <GrowthIcon size="sm" className="mt-hair" />+{formatBytes(flow.growth7dBytes)} in the
            last 7 d — about {flow.fastGrowthRatio}× its usual week.
          </p>
        )}
      </div>
    )}
  </div>
);

const Empty = () => {
  const empty = useQuery({
    queryKey: retentionKeys.empty,
    queryFn: () => storageFlows({ limit: 100, empty: true }),
  });
  if (empty.isPending) return <LoadingState rows={2} />;
  if (empty.isError) return <ErrorState error={empty.error} onRetry={() => void empty.refetch()} />;
  return (
    <>
      {empty.data.data.map((flow) => (
        <Row key={flow.flowId} flow={flow} rank={null} />
      ))}
    </>
  );
};

export const BiggestFlows = ({ levels, id }: { levels: TStorageLevels; id: string }) => {
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const needle = useDebouncedValue(q.trim(), 250);
  const list = useQuery({
    queryKey: retentionKeys.flows(needle, TOP),
    queryFn: () => storageFlows({ q: needle, limit: TOP }),
    placeholderData: keepPreviousData,
  });

  const rows = list.data?.data ?? [];
  const shown = needle || all ? rows : rows.slice(0, SHOWN);
  const nothingStored = levels.storingCount === 0;

  return (
    <div id={id} className="scroll-mt-page border-t border-border-sunken">
      <div className="flex items-center gap-inset px-card pt-4 pb-inset">
        <h3 className="text-data font-semibold text-primary">Biggest flows</h3>
        <span className="font-mono text-micro text-faint">
          top {TOP} by size · {plural(levels.storingCount, 'flow stores', 'flows store')} something
        </span>
        <Input
          size="sm"
          type="search"
          aria-label="Find any flow"
          placeholder={
            levels.flowCount > TOP
              ? `Find any of ${levels.flowCount.toLocaleString('en-US')} flows`
              : 'Find any flow'
          }
          leading={<SearchIcon size="sm" />}
          value={q}
          onChange={(event) => setQ(event.target.value)}
          className="ml-auto w-col-keeps"
        />
      </div>
      {!nothingStored && (
        <div
          className={cn(
            'flex h-row items-center gap-3 border-b border-border-sunken px-card',
            GROUP_LABEL,
          )}
        >
          <span className="w-col-rank shrink-0" />
          <span className="min-w-0 flex-1">Flow</span>
          <span className="w-col-inherits shrink-0">Folder</span>
          <span className="w-col-keeps shrink-0">Keeps</span>
          <span className="w-col-size shrink-0 text-right">Stored</span>
          <span className="w-col-growth shrink-0 text-right">Last 30 days</span>
        </div>
      )}
      {list.isPending && <LoadingState rows={4} />}
      {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}
      {shown.map((flow, index) => (
        <Row key={flow.flowId} flow={flow} rank={needle ? null : index + 1} />
      ))}
      {list.isSuccess && needle && rows.length === 0 && (
        <p className="border-t border-border-row px-card py-3 text-meta text-muted">
          No flow is called anything like “{needle}”.
        </p>
      )}
      {showEmpty && <Empty />}
      <div className="flex items-center gap-inset border-t border-border-row px-card py-3">
        <span className="font-mono text-micro text-faint">
          {nothingStored
            ? `No flow stores anything yet.${levels.draftCount > 0 ? ` ${plural(levels.draftCount, 'draft')}` : ''}`
            : needle
              ? `${plural(rows.length, 'match', 'matches')} of ${plural(levels.flowCount, 'flow')}`
              : `${shown.length} of the top ${Math.min(TOP, levels.storingCount)} shown${
                  levels.emptyCount > 0
                    ? ` · ${plural(levels.emptyCount, 'flow stores', 'flows store')} nothing yet`
                    : ''
                }${levels.flowCount > TOP ? ` · never a list of all ${levels.flowCount.toLocaleString('en-US')} — search finds any flow` : ''}`}
        </span>
        <span className="ml-auto flex gap-2">
          {levels.emptyCount > 0 && !needle && (
            <Button variant="ghost" size="xs" onClick={() => setShowEmpty(!showEmpty)}>
              {showEmpty ? 'Hide' : 'Show'} {plural(levels.emptyCount, 'empty flow')}
            </Button>
          )}
          {!needle && !all && rows.length > SHOWN && (
            <Button variant="ghost" size="xs" onClick={() => setAll(true)}>
              Show all {rows.length}
            </Button>
          )}
        </span>
      </div>
    </div>
  );
};
